// eslint-disable-next-line no-restricted-imports -- Direct HTTP calls needed for Redux session hydration outside React
import request from "superagent";

import { getAuthHeader } from "../../../utils/superagent";
import { APP_V2_APP_EVENTS, logError, logEvent } from "../../lib/analytics";
import { showErrorToast } from "../../lib/Notifications";
import { SSO_CALLBACK_PATH } from "../RipplingSso/paths";
import { fetchAuthSessionWithLock } from "./cognitoSessionLock";
import { isFirebaseSessionFallbackEnabled } from "./firebaseSessionFallbackState";
import {
  ADMIN_PROFILE_SUCCESS,
  FIREBASE_LOGIN_SUCCESS,
  FIREBASE_TOKEN_READY,
  LOGIN_REQUESTED,
  LOGIN_SUCCESS,
  LOGOUT,
} from "./sessionActionTypes";

const HCF_USER_EVENTS = {
  LOGGED_IN: "HCF Logged In",
} as const;

const consumeOverrideUserIdUrlParameter = (): string | null => {
  const searchParameters = new URLSearchParams(window.location.search);
  const userId = searchParameters.get("overrideUserId");
  if (userId) {
    searchParameters.delete("overrideUserId");
    history.replaceState(
      window.history.state,
      "",
      `${window.location.origin}${window.location.pathname}${searchParameters.toString() ? `?${searchParameters.toString()}` : ""}`
    );
    return userId;
  }

  const requestedUrlParameter = searchParameters.get("requestedUrl");
  if (requestedUrlParameter) {
    try {
      const requestedUrl = new URL(requestedUrlParameter, window.location.origin);
      const nestedParameters = new URLSearchParams(requestedUrl.search);
      const nestedUserId = nestedParameters.get("overrideUserId");

      if (nestedUserId) {
        return nestedUserId;
      }
    } catch {
      return null;
    }
  }

  return null;
};

/**
 * Fetches the user record and dispatches LOGIN_SUCCESS.
 *
 * This is a focused replacement for the legacy `get()` thunk from
 * `src/modules/session.ts`, without the Firebase token guards or
 * moment-timezone dependency chain.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
async function fetchSession(dispatch: any, userId: string) {
  localStorage.setItem("selectedUser", userId);
  dispatch({ type: LOGIN_REQUESTED });

  try {
    const response = await request
      .get(`${(globalThis as Record<string, unknown>).api}/user/get/${userId}`)
      .set(await getAuthHeader());

    dispatch({
      type: LOGIN_SUCCESS,
      data: { ...response.body },
    });
  } catch (error: unknown) {
    showErrorToast(error instanceof Error ? error.message : "Failed to fetch user session");
  }
}

/**
 * Fetches the facility user record via the backend endpoint.
 * Inlined to avoid importing from the legacy api/facilityUsers barrel.
 */
async function getFacilityUser() {
  const response = await request
    .get(`${(globalThis as Record<string, unknown>).api}/facilityUser/findByEmail`)
    .set(await getAuthHeader());
  return response.body;
}

/**
 * Initializes the user session from Cognito tokens.
 *
 * Checks for an existing valid Cognito session via Amplify and hydrates
 * the Redux session. Contains no Firebase code — Firebase session handling
 * is managed separately by `initFirebaseSession` when the fallback FF is off.
 *
 * Returns `true` if a valid Cognito session was found and restored,
 * `false` if no Cognito session exists.
 */
// Deduplicates concurrent calls to initCognitoSession. When multiple callers
// (e.g. SSO callback + AppLayout's useEffect) dispatch initCognitoSession at
// the same time, only the first call runs the actual session hydration. All
// subsequent calls return the same in-flight promise. This prevents the
// FIREBASE_TOKEN_READY: { ready: false } dispatch from firing multiple times,
// which would cause render loops and duplicate API calls.
let inFlightSessionInit: Promise<boolean> | undefined;

export const initCognitoSession =
  () =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (dispatch: any): Promise<boolean> => {
    if (inFlightSessionInit) {
      return inFlightSessionInit;
    }

    inFlightSessionInit = (async () => {
      dispatch({
        type: FIREBASE_TOKEN_READY,
        data: { ready: false },
      });

      try {
        const session = await fetchAuthSessionWithLock();
        const idToken = session.tokens?.idToken;

        if (!idToken) {
          if (!isFirebaseSessionFallbackEnabled()) {
            dispatch({ type: LOGOUT });
            dispatch({ type: FIREBASE_TOKEN_READY, data: { ready: true } });
          }
          // When Firebase fallback is enabled, leave ready: false so that
          // the app waits for initFirebaseSession's onLoggedIn callback to
          // set ready: true after the Firebase session is established.
          return false;
        }

        const email = idToken.payload?.email;
        if (typeof email !== "string") {
          if (!isFirebaseSessionFallbackEnabled()) {
            dispatch({ type: LOGOUT });
            dispatch({ type: FIREBASE_TOKEN_READY, data: { ready: true } });
          }
          // When Firebase fallback is enabled, leave ready: false (see above).
          return false;
        }

        const cognitoIdToken = idToken.toString();

        dispatch({
          type: FIREBASE_TOKEN_READY,
          data: { ready: true },
        });

        const isEmployee = email.endsWith("@clipboardhealth.com");

        await (isEmployee
          ? handleEmployeeLogin(dispatch, cognitoIdToken)
          : handleFacilityLogin(dispatch, cognitoIdToken));

        return true;
      } catch (error: unknown) {
        const isSsoCallback = globalThis.location?.pathname === SSO_CALLBACK_PATH;
        const eventName = isSsoCallback
          ? APP_V2_APP_EVENTS.RIPPLING_SSO_SESSION_HYDRATION_FAILURE
          : APP_V2_APP_EVENTS.PASSWORD_LOGIN_COGNITO_SESSION_FAILURE;
        logError(eventName, {
          error,
          metadata: { source: isSsoCallback ? "sso-callback" : "cognito-session-init" },
        });
        if (!isFirebaseSessionFallbackEnabled()) {
          dispatch({ type: LOGOUT });
          dispatch({ type: FIREBASE_TOKEN_READY, data: { ready: true } });
        }
        // When Firebase fallback is enabled, leave ready: false so that
        // initFirebaseSession's onLoggedIn callback handles it.
        return false;
      } finally {
        inFlightSessionInit = undefined;
      }
    })();

    return inFlightSessionInit;
  };

// eslint-disable-next-line @typescript-eslint/no-explicit-any
async function handleEmployeeLogin(dispatch: any, cognitoIdToken: string): Promise<void> {
  const response = await request
    .get(`${(globalThis as Record<string, unknown>).api}/user/getByEmail`)
    .set(await getAuthHeader());

  dispatch({ type: ADMIN_PROFILE_SUCCESS, data: { user: response.body } });

  const user = { ...response.body };

  const overrideUserId = consumeOverrideUserIdUrlParameter();
  if (overrideUserId) {
    localStorage.setItem("selectedUser", overrideUserId);
  }

  const selectedUser = localStorage.getItem("selectedUser");

  // FIREBASE_LOGIN_SUCCESS must be dispatched BEFORE fetchSession because the
  // LOGIN_SUCCESS reducer (in session.ts) reads state.user._id for analytics.
  // state.user is set by FIREBASE_LOGIN_SUCCESS.
  dispatch({
    type: FIREBASE_LOGIN_SUCCESS,
    data: {
      // Named `firebaseToken` for backward compatibility with legacy code,
      // but contains the Cognito ID token when using Cognito-first auth.
      firebaseToken: cognitoIdToken,
      user: {
        access: user.roles,
        _id: user._id,
        name: user.name,
        email: user.email,
        phone: user.phone,
        ...(user?.intercomHash && { intercomHash: user.intercomHash }),
      },
    },
  });

  await fetchSession(dispatch, selectedUser || user._id);
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
async function handleFacilityLogin(dispatch: any, cognitoIdToken: string): Promise<void> {
  const { user } = await getFacilityUser();
  if (!user?.facilities?.length) {
    throw new Error("Facility user has no associated facilities");
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const facilityIdsArray = user.facilities.map((facility: any) => facility.userId);

  const overrideUserId = consumeOverrideUserIdUrlParameter();
  if (overrideUserId) {
    localStorage.setItem("selectedFacility", overrideUserId);
  }

  const selectedFacility = localStorage.getItem("selectedFacility");
  const selectedUser = localStorage.getItem("selectedUser");
  const isSelectedFacilityExists = selectedFacility && facilityIdsArray.includes(selectedFacility);
  const userId = isSelectedFacilityExists ? selectedFacility : facilityIdsArray[0] || selectedUser;

  if (!isSelectedFacilityExists) {
    localStorage.setItem("selectedFacility", facilityIdsArray[0]);
    logEvent(HCF_USER_EVENTS.LOGGED_IN, {
      workplaceId: userId,
      workplaceUserId: user?._id,
    });
  }

  // FIREBASE_LOGIN_SUCCESS must be dispatched BEFORE fetchSession because the
  // LOGIN_SUCCESS reducer (in session.ts) reads state.user._id for analytics.
  // state.user is set by FIREBASE_LOGIN_SUCCESS.
  dispatch({
    type: FIREBASE_LOGIN_SUCCESS,
    data: {
      // Named `firebaseToken` for backward compatibility with legacy code,
      // but contains the Cognito ID token when using Cognito-first auth.
      firebaseToken: cognitoIdToken,
      user: {
        facilityIds: facilityIdsArray,
        tmz: user.tmz,
        access: user.roles,
        permissions: user.permissions,
        _id: user._id,
        name: user.name,
        email: user.email,
        phone: user.phone,
        notify: user.notify,
        isEmailVerified: user.isEmailVerified,
        isSuspicious: user.isSuspicious,
        ...(user?.intercomHash && { intercomHash: user.intercomHash }),
      },
    },
  });

  await fetchSession(dispatch, userId);
}
